import { Box } from '@mui/material';
import React, { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../../core-components/theme/components/Buttons';
import { PasswordInput } from '../../../core-components/theme/components/Fields';
import { StickyActionBar } from '../../../core-components/theme/components/StickyActionBar';
import { useFocusOnMount } from '../../../utilities/customHooks/useFocusOnMount';

import { AuthPanelHeading, AuthSubtitle, authContentSx, authFieldsSx, authPanelSx } from './AuthPanel';
import { IdentifierField } from './IdentifierField';
import type { IdentifierFieldState } from './IdentifyPanel';

export interface PasswordPanelProps {
  readonly title: string;
  readonly subtitle?: React.ReactNode;
  readonly field: IdentifierFieldState;
  readonly fieldProblemKey: number;
  readonly password: string;
  readonly passwordError: string | null;
  readonly passwordProblemKey: number;
  readonly loggingIn: boolean;
  readonly sending: boolean;
  readonly held: boolean;
  readonly notice?: React.ReactNode;
  readonly onPasswordChange: (value: string) => void;
  readonly onLogIn: () => void;
  readonly onForgot: () => void;
}

export const PasswordPanel: React.FC<PasswordPanelProps> = ({
  title,
  subtitle,
  field,
  fieldProblemKey,
  password,
  passwordError,
  passwordProblemKey,
  loggingIn,
  sending,
  held,
  notice,
  onPasswordChange,
  onLogIn,
  onForgot,
}) => {
  const { t } = useTranslation();
  const subtitleId = useId();
  const noticeId = useId();
  const identifierRef = useFocusOnMount<HTMLInputElement>(fieldProblemKey > 0, fieldProblemKey);
  const passwordRef = useFocusOnMount<HTMLInputElement>(true, `${passwordProblemKey}:${passwordError ?? ''}`);
  const hasSubtitle = subtitle !== undefined && subtitle !== null;

  return (
    <Box
      component="form"
      noValidate
      sx={authPanelSx}
      onSubmit={(event: React.FormEvent) => {
        event.preventDefault();
        onLogIn();
      }}
    >
      <Box sx={authContentSx}>
        <AuthPanelHeading title={title}>{hasSubtitle && <AuthSubtitle id={subtitleId}>{subtitle}</AuthSubtitle>}</AuthPanelHeading>
        <Box sx={authFieldsSx}>
          <IdentifierField
            {...field}
            inputRef={identifierRef}
            describedById={hasSubtitle ? subtitleId : undefined}
            readOnly={loggingIn}
            onEnter={() => passwordRef.current?.focus()}
          />
          <PasswordInput
            inputRef={passwordRef}
            label={t('passcodeAuth.password.label', 'Password')}
            autoComplete="current-password"
            size="medium"
            readOnly={loggingIn}
            value={password}
            onValueChange={onPasswordChange}
            onKeyDown={(event: React.KeyboardEvent<HTMLElement>) => {
              if (event.key !== 'Enter') return;
              event.preventDefault();
              onLogIn();
            }}
            inputProps={{ enterKeyHint: 'go' }}
            error={passwordError !== null}
            helperText={passwordError ?? undefined}
          />
        </Box>
        <Box id={noticeId}>{notice}</Box>
      </Box>
      <StickyActionBar
        layout="dock"
        announce={false}
        tertiary={
          <Button
            type="button"
            intent="tertiary"
            busy={sending}
            aria-disabled={loggingIn || held || undefined}
            aria-describedby={held ? noticeId : undefined}
            onClick={onForgot}
          >
            {t('passcodeAuth.password.forgot', 'Forgot password? Send me a code')}
          </Button>
        }
        primary={
          <Button type="submit" intent="primary" busy={loggingIn} aria-disabled={sending || undefined}>
            {t('passcodeAuth.password.logIn', 'Log in')}
          </Button>
        }
      />
    </Box>
  );
};
